import { useState, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  FloppyDiskIcon,
  PlugIcon,
  InfoIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import {
  useGetIntegration,
  useUpdateIntegration,
  useTestJiraConnection,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import {
  IntegrationAvailability,
  IntegrationKey,
  JiraIntegrationSettings as JiraSettings,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';

export function JiraIntegrationSettings({
  availability: _availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
  } = useGetIntegration(IntegrationKey.Jira);
  const updateIntegration = useUpdateIntegration();
  const testConnection = useTestJiraConnection();

  const [formData, setFormData] = useState<JiraSettings>({
    baseUrl: '',
    serviceAccountEmail: '',
    apiToken: '',
    defaultProjectKey: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'untested' | 'success' | 'failed'
  >('untested');
  const [hasExistingApiToken, setHasExistingApiToken] = useState(false);

  useEffect(() => {
    if (integration?.settings) {
      const settings = integration.settings as JiraSettings;
      setFormData({
        ...settings,
        // Never populate the token field with the obfuscated sentinel
        apiToken: '',
      });
      if (settings.apiToken) {
        setHasExistingApiToken(true);
      }
    }
  }, [integration]);

  const handleFieldChange = (field: keyof JiraSettings, value: string) => {
    setConnectionStatus('untested');
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = (): string | null => {
    if (!formData.baseUrl) return 'Base URL is required.';
    if (!formData.serviceAccountEmail)
      return 'Service account email is required.';
    if (!formData.defaultProjectKey) return 'Default project key is required.';
    if (!hasExistingApiToken && !formData.apiToken)
      return 'API token is required.';
    return null;
  };

  const buildSettingsToSend = (): JiraSettings => {
    // Omit apiToken when the user didn't type a new one — the backend
    // preserves the existing encrypted value.
    const { apiToken, ...rest } = formData;
    return apiToken ? { ...rest, apiToken } : rest;
  };

  const handleSave = async () => {
    const errorMessage = validate();
    if (errorMessage) {
      showToast({
        title: 'Validation Error',
        message: errorMessage,
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateIntegration.mutateAsync({
        key: IntegrationKey.Jira,
        settings: buildSettingsToSend(),
      });
      showToast({
        title: 'Settings Saved',
        message: 'Jira connection settings have been saved.',
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: 'Save Failed',
        message: 'Failed to save settings. Please try again.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    const errorMessage = validate();
    if (errorMessage) {
      showToast({
        title: 'Validation Error',
        message: errorMessage,
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsSaving(true);
    try {
      const saved = await updateIntegration.mutateAsync({
        key: IntegrationKey.Jira,
        settings: buildSettingsToSend(),
      });

      const result = await testConnection.mutateAsync({
        integrationId: saved.id,
      });
      setConnectionStatus(result.success ? 'success' : 'failed');
      showToast({
        title: result.success ? 'Connection Successful' : 'Connection Failed',
        message: result.message,
        color: result.success ? 'success' : 'error',
        icon: result.success ? 'check' : 'error',
      });
    } catch {
      setConnectionStatus('failed');
      showToast({
        title: 'Connection Test Failed',
        message: 'An unexpected error occurred while testing the connection.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="loading message while Jira integration settings are fetched">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="error message when Jira integration settings fail to load">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  const isConfigured =
    !!integration?.settings &&
    !!(integration.settings as JiraSettings).baseUrl &&
    !!(integration.settings as JiraSettings).serviceAccountEmail &&
    !!(integration.settings as JiraSettings).defaultProjectKey &&
    hasExistingApiToken;

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center gap-3">
        {isConfigured && connectionStatus === 'success' ? (
          <>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="font-medium">
                <T context="Jira integration status label when connection test succeeded">
                  Integration Active
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-green-100 text-green-700 border-green-300"
            >
              <T context="Jira integration badge when successfully connected">
                Connected
              </T>
            </Badge>
          </>
        ) : connectionStatus === 'failed' ? (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-red-500" />
              <span className="font-medium">
                <T context="Jira integration status label when connection test failed">
                  Connection Failed
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-red-50 text-red-700 border-red-300"
            >
              <T context="Jira integration badge prompting user to verify credentials after failure">
                Check Credentials
              </T>
            </Badge>
          </>
        ) : isConfigured ? (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="font-medium">
                <T context="Jira integration status when credentials saved but not tested">
                  Credentials Saved
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Jira integration badge when connection not yet verified">
                Not Verified
              </T>
            </Badge>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="font-medium">
                <T context="Jira integration status when not yet configured">
                  Not Configured
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Jira integration badge when user must enter credentials">
                Credentials Required
              </T>
            </Badge>
          </>
        )}
      </div>

      {/* Setup Instructions — open by default until credentials exist */}
      <Accordion
        type="single"
        collapsible
        defaultValue={isConfigured ? undefined : 'setup'}
      >
        <AccordionItem
          value="setup"
          className="border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Jira setup instructions accordion title">
                Setup Instructions — read before configuring
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-4 text-sm text-blue-900">
              <p>
                <T context="Jira setup instructions intro paragraph">
                  This integration files issues from workflows (via the{' '}
                  <Var>
                    <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                      create_jira_issue
                    </code>
                  </Var>{' '}
                  step) and assigns them to the matching Jira user by email.
                  You&apos;ll need admin access to an Atlassian Cloud site and a
                  target Jira project before starting.
                </T>
              </p>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Jira setup step 1 heading">
                    1. Create a bot Atlassian user (recommended)
                  </T>
                </p>
                <p className="mb-2">
                  <T context="Jira setup step 1 description">
                    Use a dedicated Atlassian account instead of a real
                    person&apos;s — issue history stays clean and the
                    integration survives staff turnover. Pick an email you
                    control (e.g.{' '}
                    <Var>
                      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                        jira-bot@your-company.com
                      </code>
                    </Var>
                    ), then in{' '}
                    <Var>
                      <a
                        href="https://admin.atlassian.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline hover:text-blue-700"
                      >
                        admin.atlassian.com
                        <ArrowSquareOutIcon className="h-3 w-3" />
                      </a>
                    </Var>{' '}
                    → User management → Invite users, invite that email and
                    grant access to your Jira site. Accept the invite from the
                    bot&apos;s inbox to finish account creation.
                  </T>
                </p>
                <p className="text-xs italic">
                  <T context="Jira setup step 1 testing note">
                    You can skip this and use your personal account if you just
                    want to test.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Jira setup step 2 heading">
                    2. Grant the bot project access
                  </T>
                </p>
                <p>
                  <T context="Jira setup step 2 description">
                    Inside your target Jira project, go to{' '}
                    <Var>
                      <strong>Project settings → Access</strong>
                    </Var>{' '}
                    and add the bot user with a role that includes{' '}
                    <Var>
                      <strong>Browse Projects</strong>
                    </Var>
                    ,{' '}
                    <Var>
                      <strong>Create Issues</strong>
                    </Var>
                    , and{' '}
                    <Var>
                      <strong>Assign Issues</strong>
                    </Var>
                    . The built-in <Var>Member</Var> or <Var>Administrator</Var>{' '}
                    roles cover all three.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Jira setup step 3 heading">
                    3. Generate a classic API token
                  </T>
                </p>
                <AlertContainer className="mb-2 border-amber-300 bg-amber-50 text-amber-900">
                  <WarningCircleIcon className="h-4 w-4" />
                  <AlertDescription className="text-amber-900">
                    <T context="Jira setup classic vs scoped token warning">
                      Use the <strong>classic (unscoped)</strong> token, not a
                      &quot;scoped&quot; or service-account token. Classic
                      tokens start with{' '}
                      <Var>
                        <code className="rounded bg-amber-100 px-1 py-0.5 text-xs font-mono">
                          ATATT…
                        </code>
                      </Var>{' '}
                      and work with HTTP Basic auth. Scoped tokens (
                      <Var>
                        <code className="rounded bg-amber-100 px-1 py-0.5 text-xs font-mono">
                          ATSTT…
                        </code>
                      </Var>
                      ) use OAuth 2.0 and are not supported here.
                    </T>
                  </AlertDescription>
                </AlertContainer>
                <p>
                  <T context="Jira setup step 3 description">
                    Sign in to{' '}
                    <Var>
                      <a
                        href="https://id.atlassian.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline hover:text-blue-700"
                      >
                        id.atlassian.com
                        <ArrowSquareOutIcon className="h-3 w-3" />
                      </a>
                    </Var>{' '}
                    <strong>as the bot user</strong>, then open{' '}
                    <Var>
                      <a
                        href="https://id.atlassian.com/manage-profile/security/api-tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline hover:text-blue-700"
                      >
                        Profile → Security → API tokens
                        <ArrowSquareOutIcon className="h-3 w-3" />
                      </a>
                    </Var>
                    . Click <strong>Create API token</strong> (the unscoped
                    option — not &quot;Create API token with scopes&quot;),
                    label it, and copy the token immediately. Atlassian only
                    shows it once.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Jira setup step 4 heading">
                    4. Find your project key
                  </T>
                </p>
                <p>
                  <T context="Jira setup step 4 description">
                    The project key is the uppercase prefix on every issue ID —
                    the{' '}
                    <Var>
                      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                        ENG
                      </code>
                    </Var>{' '}
                    in{' '}
                    <Var>
                      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                        ENG-1234
                      </code>
                    </Var>
                    . Find it under <strong>Project settings → Details</strong>,
                    or just look at any existing issue ID.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Jira setup step 5 heading">
                    5. Fill in the form below and test
                  </T>
                </p>
                <p>
                  <T context="Jira setup step 5 description">
                    Enter the values from steps 1–4 in the fields below, click{' '}
                    <strong>Save Settings</strong>, then{' '}
                    <strong>Test Connection</strong>. A green &quot;Connection
                    Successful&quot; toast means you&apos;re done.
                  </T>
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="recipients"
          className="mt-2 border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Jira recipient discoverability accordion title">
                Making sure recipients are discoverable
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2 text-sm text-blue-900">
              <p>
                <T context="Jira recipient discoverability intro">
                  Workflow steps assign issues by looking up the
                  recipient&apos;s email in Jira. For that lookup to succeed
                  each recipient must:
                </T>
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>
                  <T context="Jira recipient requirement 1 — invited to site">
                    Have a Jira account on the same site (invited and accepted).
                  </T>
                </li>
                <li>
                  <T context="Jira recipient requirement 2 — email visibility">
                    Set their email visibility to <strong>Anyone</strong> under
                    their Atlassian <strong>Profile → Email visibility</strong>.
                    If it&apos;s set to &quot;Only you&quot; or &quot;Your
                    organization&quot;, the bot can&apos;t see the email and the
                    step will fail with{' '}
                    <Var>
                      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                        No Jira user found for email &quot;…&quot;
                      </code>
                    </Var>
                    .
                  </T>
                </li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="troubleshooting"
          className="mt-2 border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Jira troubleshooting accordion title">
                Troubleshooting
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="text-sm text-blue-900">
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <T context="Jira troubleshoot 401 client must be authenticated">
                    <strong>
                      401 / &quot;Client must be authenticated&quot;
                    </strong>{' '}
                    — API token is invalid, expired, or the wrong type.
                    Regenerate a classic (unscoped) token.
                  </T>
                </li>
                <li>
                  <T context="Jira troubleshoot 401 OAuth header">
                    <strong>
                      401 with{' '}
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          www-authenticate: OAuth
                        </code>
                      </Var>
                    </strong>{' '}
                    — you used a scoped or service-account token. Switch to a
                    classic API token.
                  </T>
                </li>
                <li>
                  <T context="Jira troubleshoot 403 on test connection">
                    <strong>403 on Test Connection</strong> — bot user
                    doesn&apos;t have product access to the Jira site. Re-check
                    step 1.
                  </T>
                </li>
                <li>
                  <T context="Jira troubleshoot 403 on issue creation">
                    <strong>
                      Step fails with{' '}
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          Jira issue creation failed: 403
                        </code>
                      </Var>
                    </strong>{' '}
                    — bot user lacks <strong>Create Issues</strong> permission
                    in the target project. Re-check step 2.
                  </T>
                </li>
                <li>
                  <T context="Jira troubleshoot no user found">
                    <strong>
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          No Jira user found for email &quot;…&quot;
                        </code>
                      </Var>
                    </strong>{' '}
                    — recipient isn&apos;t on the Jira site, or their email
                    visibility is restricted. See &quot;Making sure recipients
                    are discoverable&quot; above.
                  </T>
                </li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Jira integration settings card title">
              Connection Configuration
            </T>
          </CardTitle>
          <CardDescription>
            <T context="Jira integration settings card description">
              Configure credentials and the linked project for Jira Cloud
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="jira-base-url">
              <T context="form label for Jira Cloud site base URL field">
                Base URL
              </T>
            </Label>
            <Input
              id="jira-base-url"
              type="url"
              placeholder={gt('https://your-company.atlassian.net', {
                context: 'placeholder example Jira Cloud site URL',
              })}
              value={formData.baseUrl}
              onChange={(e) => handleFieldChange('baseUrl', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jira-service-account-email">
              <T context="form label for Jira service account email">
                Service Account Email
              </T>
            </Label>
            <Input
              id="jira-service-account-email"
              type="email"
              placeholder={gt('jira-bot@company.com', {
                context: 'placeholder for Jira service account email',
              })}
              value={formData.serviceAccountEmail}
              onChange={(e) =>
                handleFieldChange('serviceAccountEmail', e.target.value)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jira-api-token">
              <T context="form label for Jira API token field">API Token</T>
            </Label>
            <Input
              id="jira-api-token"
              type="password"
              placeholder={
                hasExistingApiToken
                  ? gt('Token saved — enter new value to change', {
                      context:
                        'API token field placeholder when a saved token already exists',
                    })
                  : gt(
                      'Generated at id.atlassian.com/manage-profile/security/api-tokens',
                      {
                        context:
                          'placeholder pointing user to where to generate a Jira API token',
                      },
                    )
              }
              value={formData.apiToken ?? ''}
              onChange={(e) => handleFieldChange('apiToken', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jira-default-project-key">
              <T context="form label for Jira default project key">
                Default Project Key
              </T>
            </Label>
            <Input
              id="jira-default-project-key"
              type="text"
              placeholder={gt('e.g. ENG', {
                context: 'placeholder example for Jira project key',
              })}
              value={formData.defaultProjectKey}
              onChange={(e) =>
                handleFieldChange('defaultProjectKey', e.target.value)
              }
            />
            <p className="text-xs text-muted-foreground">
              <T context="help text for Jira default project key">
                The Jira project where workflow-created issues will be filed.
              </T>
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="button label while Jira settings are saving">
                    Saving...
                  </T>
                </>
              ) : (
                <>
                  <FloppyDiskIcon className="h-4 w-4 mr-2" />
                  <T context="button label to save Jira connection settings">
                    Save Settings
                  </T>
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testConnection.isPending || isSaving}
            >
              {testConnection.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="button label while Jira connection test runs">
                    Testing...
                  </T>
                </>
              ) : (
                <>
                  <PlugIcon className="h-4 w-4 mr-2" />
                  <T context="button label to run Jira connection test">
                    Test Connection
                  </T>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
